import Page from '../components/page/Page'
import Logger from "../components/Logger";
import {observer} from "mobx-react";
import RoutesConfig from '../RoutesConfig';
import SignInDialog from '../components/signIn/SignInDialog';
import Tracking from '../components/Tracking';
import { Grid, Paper, Typography, useTheme} from '@mui/material';
import {useLocation, useNavigate} from 'react-router-dom';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useResources} from '../stores/ResourceProvider';
import PasswordAssistDialog from '../components/signIn/PasswordAssistDialog';
import {CreateUserSettingInput, UpdateUserSettingInput, UserStatus, UserType} from "../API";
import CheckAlt from "../images/CheckAlt.png";
import MarginRow from "../components/page/MarginRow";
import Footer from "../components/page/Footer";
import useNavigationHelper from "../components/NavigationHelper";
import {globalColors} from "../styles/globalStyles";
import Review, {ReviewStatus} from "../model/Review";
import AnnualReviewDialog from "./review/AnnualReviewDialog";
import {UserSettingType} from "../model/UserSetting";
import {getISODateToday} from "../stores/StoreUtilities";
import config from "react-global-configuration";
import useIsMobile from "../components/useIsMobile";

enum SignInPageModes {
  signIn = 'signIn',
  forgotPassword = 'forgotPassword',
  annualReview = 'annualReview'
}

interface ISignInPageProps {
  route?: string
}

const LatestTipDate = "2025-01-01"
const tipsEnabled = false

const SignInPage = observer((props: ISignInPageProps) => {
  const { accountStore, userStore, notify } = useResources()
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<SignInPageModes>(SignInPageModes.signIn)
  const [timeout, setTimeout] = useState<boolean>(false)
  // If a route is set, assume we are navigating to a protected route that requires signIn first.
  // Thus, display the signIn as a modal over the page, until they log in.
  const { route } = props

  const location = useLocation()
  const navigate = useNavigate()
  const {navigateHome} = useNavigationHelper()
  const isMobile = useIsMobile()

  const theme = useTheme()
  const styles = {
    checkBullet: {
      width: 24,
      height: 24,
      margin: theme.spacing(0.5)
    }
  }

  useEffect(() => {
    if (location) {
      const searchParams = new URLSearchParams(location.search)
      const timeout = searchParams.get("timeout")
      if (timeout && timeout === "1") {
        setTimeout(true)
      }
    }
  }, [location])

  // Logger.debug("SignInPage: mode", mode)
  // Logger.debug("SignInPage: isModal", isModal)

  const checkVersion = async () => {
    if (!userStore.isAuthenticated || config.get("domain") === "localhost") {
      return
    }

    try {
      // console.log('Checking for new version...');
      const localVersion = config.get('version')
      // console.log('Local Version:', localVersion);
      const response = await fetch('/manifest.json');
      const data = await response.json();
      const remoteVersion = data['version']
      // console.log('Remote Version:', remoteVersion);

      if (localVersion && remoteVersion && remoteVersion !== localVersion) {
        // console.log(`New version available ${remoteVersion}`);
        notify.show("info",`A new version of RBC is available. Refresh the page to update.`)
      }

    } catch (error) {
      console.error('Failed to check for new version', error);
    }
  };

  const handleNavigate = useCallback(async () => {
    let pathname

    const user = userStore.user
    if (user) {
      // Check for new version
      await checkVersion()
    }

    if (user && user.isCustomer) {
      // check for unsubmitted review
      if (user.reviews.length > 0) {
        const review = user.reviews[0]
        if (review && review.status !== ReviewStatus.Declined && !review.submittedOn) {
          pathname = RoutesConfig.review.pathname
        }
      }
      if (!pathname) {
        if (user.userType !== UserType.Client) {
          // TODO: Annual Review enable or remove
          // if (date.getFullYear() >= 2024) { // TODO: Change to 2025
          //   const year = date.getFullYear().toString()
          //   const annualReviewSetting = user.getUserSetting(UserSettingType.annualReview, year)
          //   if (!annualReviewSetting || annualReviewSetting.settings["status"] === ReviewStatus.Pending) {
          //     setMode(SignInPageModes.annualReview)
          //     return
          //   }
          // }
        }
      }
    }

    if (!pathname && tipsEnabled) {
      if (user && user.userType !== UserType.Free && user.userType !== UserType.Client) {
        // Look for typs
        const tipSettings = user.getUserSetting(UserSettingType.tipSettings, "tips")
        if (!tipSettings || (tipSettings as any)["viewedAt"] < LatestTipDate) {
          const pageViewSettings = {
            chatOpen: true,
            chatView: "FinnTips"
          }
          window.localStorage.setItem(`${userStore.user!.id}.PageViewSettings`, JSON.stringify(pageViewSettings))

          if (tipSettings) {
            const input: UpdateUserSettingInput = {
              id: tipSettings.id,
              settings: JSON.stringify({
                viewedAt: getISODateToday()
              })
            }
            const update = await userStore.updateUserSetting(input)
            if (update) {
              user.updateUserSetting(update)
            }
          } else {
            const input: CreateUserSettingInput = {
              accountId: user.accountId,
              userId: user.id,
              ownerId: user.id,
              name: "tips",
              settingType: UserSettingType.tipSettings,
              settings: JSON.stringify({
                viewedAt: getISODateToday()
              })
            }
            const update = await userStore.createUserSetting(input)
            if (update) {
              user.updateUserSetting(update)
            }
          }
        }
      }
    }

    if (!pathname) {
      if (route) {
        pathname = route
      } else {
        pathname = RoutesConfig.calc.pathname
      }
    }

    Logger.debug(`SignInPage routing to ${pathname}`)
    navigate(pathname)
  }, [navigate, route])

  useEffect(() => {
    // Show progress while the stores are loanding and user authentication is checked in App.startup.
    // progress.show("SignInPage")

    if (!userStore!.isLoading && !accountStore!.isLoading && userStore!.isAuthenticated) {
      Logger.debug("SignInPage: Completed loading user")
      // progress!.hide("SignInPage")
      setIsLoading(false)
      if (userStore.user && userStore.user.userStatus !== UserStatus.Cancelled && userStore.user.userStatus !== UserStatus.Deleted) {
        handleNavigate()
      }
    }

    /*
        when(
          // Once
          () => userStore!.isLoading === false && accountStore!.isLoading === false,
          // Then
          () => {
            Logger.debug("SignInPage: App completed loading")
            progress!.hide("SignInPage")
            setIsLoading(false)
          }
        )
        when(
          // Once
          () => !userStore!.isLoading && !accountStore!.isLoading && userStore!.isAuthenticated,
          // Then
          () => {
            Logger.debug("SignInPage: Completed loading user")
            progress!.hide("SignInPage")
            setIsLoading(false)
            if (userStore.user && userStore.user.userStatus !== UserStatus.Cancelled && userStore.user.userStatus !== UserStatus.Deleted) {
              handleNavigate()
            }
          }
        )
     */
  }, [accountStore, userStore, userStore.isLoading, userStore.isAuthenticated])

  const onSignIn = () => {
    Logger.debug('onSignIn')
    handleNavigate()
  }

  const onClose = () => {
    navigateHome()
  }

  const onShowPasswordAssist = () => {
    Tracking.pageView("PasswordAssistDialog")
    setMode(SignInPageModes.forgotPassword)
  }

  const onShowSignUp = () => {
    Tracking.pageView("SignupDialog")
    navigate(RoutesConfig.signUp.pathname!)
  }

  const onShowSignIn = () => {
    Tracking.pageView("SignInDialog")
    setMode(SignInPageModes.signIn)
  }

  const handleReviewStatusChange = (review?: Review) => {
    if (review) {
      navigate(RoutesConfig.review.pathname)
    } else {
      navigate(RoutesConfig.calc.pathname)
    }
  }

  const renderPromo = () => {

    return (
      <Paper
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          overflow: 'hidden',
          ml: {
            md: 2
          },
          mt: {
            md: 3
          },
          p: 2,
          maxWidth: {
            md: theme.breakpoints.values.lg
          },
          // minHeight: 532
        }}
        elevation={2}
      >
        <Grid container direction="column" spacing={2}>
          <Grid item textAlign="center">
            <Typography variant="body2" sx={{color:globalColors.premium, fontWeight:600}}>
              WELCOME TO
            </Typography>
            <Typography variant="h2" color="primary">Retirement Budget Calculator <b style={{color:globalColors.premium}}>Pro!</b></Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1"><b>Retirement Budget Calculator Pro</b> is more powerful and easier to use than ever, helping you achieve your goals and live the life you want!</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" gutterBottom><b>RBC Pro features:</b></Typography>
            <Typography variant="body1"><img src={CheckAlt} alt="Check" style={styles.checkBullet}/> Completely redesigned, easy to use interface</Typography>
            <Typography variant="body1"><img src={CheckAlt} alt="Check" style={styles.checkBullet}/> Advanced visualizations of your retirement plan</Typography>
            <Typography variant="body1"><img src={CheckAlt} alt="Check" style={styles.checkBullet}/> Retirement Optimizer to fine-tune your plan</Typography>
            <Typography variant="body1"><img src={CheckAlt} alt="Check" style={styles.checkBullet}/> Retirement Intelligence now available 24/7</Typography>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  // if (isMobile) {
  //   return (
  //     <Page title="Sign in">
  //       <MarginRow maxWidth={theme.breakpoints.values.xl}>
  //         <Box sx={{display:"flex", alignItems:"center", justifyContent:"center"}}>
  //           <MobileNotice action="mobile_sign_in" onCancel={() => navigateHome()}/>
  //         </Box>
  //       </MarginRow>
  //     </Page>
  //   )
  // }

  return (
    <Page title="Sign in">
      <MarginRow maxWidth={theme.breakpoints.values.xl}>
        { isLoading === false &&
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="top"
            sx={{
              minHeight: 'calc(100vh - 206px)',
              my: {
                sm: 0,
                md: "auto"
              },
              px: {
                sm: 2,
                md: 0
              },
              py: {
                sm: 0,
                md: 4,
              },
            }}
            spacing={6}
          >
            {!isMobile &&
              <Grid item md={6} sx={{display: {xs:'none', sm:'none', md:'block'}}}>
                {renderPromo()}
              </Grid>
            }
            <Grid item sm={12} md={6}>
              { mode === 'signIn' &&
                <SignInDialog
                  onSignIn={onSignIn}
                  onClose={onClose}
                  onShowPasswordAssist={onShowPasswordAssist}
                  onShowSignUp={onShowSignUp}
                  isShownAsModal={ false }
                  timeout={timeout}
                />
              }
              { mode === 'forgotPassword' &&
                <PasswordAssistDialog
                  onClose={onClose}
                  onShowSignIn={onShowSignIn}
                  isShownAsModal={ false }
                />
              }
              { mode === SignInPageModes.annualReview &&
                <AnnualReviewDialog user={userStore.user}
                                    onChange={handleReviewStatusChange}
                />
              }
            </Grid>
          </Grid>
        }
        <Footer/>
      </MarginRow>
    </Page>
  )

})

export default SignInPage